<script setup>
import { computed, onMounted, ref, watch } from "vue";
import moment from "moment";
import { dashboardStore } from "@/_store/dashboardStore";
import ArgonCalendar from "@/components/ArgonCalendar.vue";
import ProgressBarCardDashboard from "./components/ProgressBarCardDashboard.vue";
import HourlyProgressChart from "./components/HourlyProgressChart.vue";
import FleetTable from "./components/FleetTable.vue";

const store = dashboardStore();
const currentDate = ref(moment().format("YYYY-MM-DD"));

const overviewCards = computed(() => {
  const overview = store.overview || {};
  const totalRoutes = toSafeNumber(overview.total_routes);
  const totalStops = toSafeNumber(overview.total_stop);
  const totalRouteStops = toSafeNumber(overview.total_route_stops);

  return [
    {
      key: "unassigned_stops",
      title: "Paradas sin asignar",
      value: toSafeNumber(overview.unassigned_stops),
      current: toSafeNumber(overview.unassigned_stops),
      total: totalStops,
      progressColor: "pendiente",
    },
    {
      key: "routes",
      title: "Rutas",
      value: totalRoutes,
      current: toSafeNumber(overview.active_routes),
      total: totalRoutes,
      progressColor: "en_curso",
    },
    {
      key: "sla_routes_at_risk",
      title: "Rutas SLA en riesgo",
      value: toSafeNumber(overview.sla_routes_at_risk),
      current: toSafeNumber(overview.sla_routes_at_risk),
      total: totalRoutes,
      progressColor: "error",
    },
    {
      key: "pending_route_stops",
      title: "Paradas en ruta pendientes",
      value: toSafeNumber(overview.pending_route_stops),
      current: toSafeNumber(overview.pending_route_stops),
      total: totalRouteStops,
      progressColor: "pendiente",
    },
    {
      key: "completed_route_stops",
      title: "Paradas en ruta completadas",
      value: toSafeNumber(overview.completed_route_stops),
      current: toSafeNumber(overview.completed_route_stops),
      total: totalRouteStops,
      progressColor: "finalizado",
    },
    {
      key: "incidence_route_stops",
      title: "Paradas en ruta con incidencia",
      value: toSafeNumber(overview.incidence_route_stops),
      current: toSafeNumber(overview.incidence_route_stops),
      total: totalRouteStops,
      progressColor: "error",
    },
  ].map((item) => ({
    ...item,
    percentage: calculatePercentage(item.current, item.total),
  }));
});

const progressRows = computed(() =>
  (Array.isArray(store.progress) ? store.progress : []).map((item, index) => {
    const total = toSafeNumber(item?.total);
    const completed = toSafeNumber(item?.completed);
    const pending = toSafeNumber(item?.pending);
    const incidence = toSafeNumber(item?.incidence);
    return {
      _id: item?.hour || `progress-${index}`,
      hour: item?.hour || "-",
      total,
      completed,
      pending,
      incidence,
    };
  })
);

const fleetItems = computed(() => ({
  total: Array.isArray(store.fleet) ? store.fleet.length : 0,
  data: Array.isArray(store.fleet)
    ? [...store.fleet]
        .sort((left, right) =>
          String(left?.vehicle_name || "")
            .localeCompare(String(right?.vehicle_name || ""), undefined, {
              sensitivity: "base",
              numeric: true,
            })
        )
        .map((item, index) => ({
        ...item,
        _id:
          item?._id ||
          item?.vehicle_abbreviation ||
          item?.vehicle_name ||
          `fleet-${index}`,
        stops_total: toSafeNumber(item?.stops_total),
        stops_completed: toSafeNumber(item?.stops_completed),
        alerts_count: toSafeNumber(item?.alerts_count),
        progress_percent: normalizePercent(item?.progress_percent),
      }))
    : [],
}));

onMounted(() => {
  store.getDashboard(currentDate.value);
});

watch(
  () => currentDate.value,
  (value, oldValue) => {
    if (!value || value === oldValue) return;
    store.getDashboard(value);
  }
);

function toSafeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function calculatePercentage(value, total) {
  if (!total || total <= 0) return 0;
  const percentage = (toSafeNumber(value) / toSafeNumber(total)) * 100;
  return Math.max(0, Math.min(100, Math.round(percentage)));
}

function normalizePercent(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.min(100, Math.round(parsed)));
}
</script>

<template>
  <div class="container-fluid">
    <div class="row justify-content-center mb-3">
      <div class="col-12 col-sm-8 col-md-3 col-lg-2 dashboard-date-card-col">
        <div class="card card-detail-layout">
          <div class="card-body">
            <argon-calendar v-model="currentDate" />
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3 mb-3">
      <div
        v-for="card in overviewCards"
        :key="card.key"
        class="col-12 col-md-6 col-xl-2"
      >
        <progress-bar-card-dashboard
          :title="card.title"
          :value="card.value"
          :current="card.current"
          :total="card.total"
          :percentage="card.percentage"
          :progress-color="card.progressColor"
          :meta-label="card.metaLabel"
        />
      </div>
    </div>

    <div class="row g-3">
      <div class="col-12 col-xl-5">
        <hourly-progress-chart :items="progressRows" />
      </div>

      <div class="col-12 col-xl-7">
        <fleet-table :items="fleetItems" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.dashboard-date-card-col {
  max-width: 10rem;
}
</style>
